import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { clearPresetNote, setPresetNote } from 'shared/store/slices/presetNoteSlice';
import { updateTradePartner } from 'shared/store/slices/tradePartnerSlice';

/** Deep comparison and empty value checking. */
import isEqual from 'lodash/isEqual';

/** Material UI Packages. */
import { Box, Dialog, FormControlLabel, Paper, styled, Switch, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Internal Components. */
import CircularLoading from 'shared/ui/CircularLoading';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import DialogHeader from 'shared/ui/DialogHeader';
import { SlideUpTransition } from 'shared/ui/Transition';
import { updateSocketData, readSocketData } from 'shared/lib/fetchSocket';
import { getSocket } from 'socket';
import validateFields from 'shared/lib/validateFields';

import PartnerInfo from './PartnerInfo';
import ApplySetting from './ApplySetting';
import { normalizeAlias } from './AliasList';
import { initTradePartnerValidator } from 'data/tradePartnerConfig';

/** Color theme for active switch component. */
const ActiveSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#ffffff',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#8fcbb3',
    },
}));

/** How long a save may wait for the server's answer before the dialog is released. */
const ACK_TIMEOUT_MS = 10000;

const NO_ANSWER_MESSAGE =
    'The server did not answer. Please check whether the change was saved before trying again.';

/**
 * Guards the callbacks handed to the socket helper so the caller always hears back exactly once.
 *
 * The plain ack callback the helper registers is silently dropped by socket.io when the
 * connection closes, and it never fires if the server hangs. A dialog that locks itself until
 * the ack arrives would then stay locked until a page refresh. This adds the two missing exits
 * (an ack timeout and a `disconnect` listener) and folds every outcome into one settle step,
 * so the lock is released in every case. Pass the returned onSuccess/onFailure to the helper;
 * `onFinally` runs here, once, after whichever outcome wins.
 */
const guardSocketAck = (dispatch, { onSuccess, onFailure, onFinally, timeoutMs = ACK_TIMEOUT_MS }) => {
    const socket = getSocket();
    let settled = false;
    let timer = null;

    const onDisconnect = () => giveUp();

    const settle = (finish) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        socket?.off?.('disconnect', onDisconnect);
        finish();
        onFinally?.();
    };

    function giveUp() {
        settle(() => {
            onFailure?.();
            dispatch(setSnackbar({ open: true, message: NO_ANSWER_MESSAGE, severity: 'error', autoHideDuration: 10000 }));
        });
    }

    socket?.on?.('disconnect', onDisconnect);
    timer = setTimeout(giveUp, timeoutMs);

    return {
        onSuccess: (response) => settle(() => onSuccess?.(response)),
        onFailure: (response) => settle(() => onFailure?.(response)),
    };
};

export default function EditTradePartnerDialog(props) {
    /** Get props defined in TradePartner.js. */
    const { selectedRow, setSelectedRow, editOpen, setEditOpen } = props;

    /** Dispatch a function to update an edited trade partner. */
    const dispatch = useDispatch();

    /** Load redux state to select the trade partner being edited from the full trade partner list. */
    const tradePartnerData = useSelector((state) => state.tradePartnerForm);
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);

    /** Partner type dropdown options from server (read `il_trade_partner` / `partnerTypeOptions`). */
    const [partnerTypeOptions, setPartnerTypeOptions] = useState([]);
    /** Load preset note redux state. */
    const presetNoteData = useSelector((state) => state.presetNoteForm);
    /** State for trade partner input data. */
    const [tradePartnerForm, setTradePartnerForm] = useState({});
    /** State for apply setting input data. */
    const [applySettingForm, setApplySettingForm] = useState([]);

    /** State for validate required input data. */
    const [tradePartnerValidator, setTradePartnerValidator] = useState(initTradePartnerValidator);
    /** State for comparing original data and updated data. */
    const [originalData, setOriginalData] = useState({ tradePartner: {}, applySetting: [] });

    /** State for loading circular progress load. */
    const [saveLoad, setSaveLoad] = useState(false);
    /**
     *  State to track whether values have changed from their initial state.
     *
     * - `isOriginDirty` detects changes in the entire `tradePartnerForm` and `applySettingForm`.
     * - `isPresetDirty` detects changes in the values used to render the preset chips.
     */
    const [dirtyFlags, setDirtyFlags] = useState({ isOriginDirty: false, isPresetDirty: false });

    const resetDialogState = () => {
        setEditOpen(false);
        setTradePartnerForm({});
        setApplySettingForm([]);
        setPartnerTypeOptions([]);
        dispatch(clearPresetNote());

        // Initialize validator state.
        setOriginalData({ tradePartner: {}, applySetting: [] });
        setTradePartnerValidator(initTradePartnerValidator);

        // Initialize other states.
        setSelectedRow(null);
        setDirtyFlags({ isOriginDirty: false, isPresetDirty: false });
    };

    /** Handler for active change state. */
    const handleActiveChange = (event) => {
        setTradePartnerForm((prev) => ({
            ...prev,
            status: event.target.checked,
        }));
    };

    /** Marks the aliases the server refused because another trade partner already holds them. */
    const markAliasConflicts = (response) => {
        const conflicts = response?.status === 409 ? response.data?.conflicts : null;
        if (!Array.isArray(conflicts) || conflicts.length === 0) return;

        const ownerByAlias = new Map(
            conflicts.map((conflict) => [normalizeAlias(conflict.alias), conflict.trade_partner_name || 'another partner'])
        );
        setTradePartnerForm((prev) =>
            Array.isArray(prev.il_trade_partner_alias)
                ? {
                      ...prev,
                      il_trade_partner_alias: prev.il_trade_partner_alias.map((item) => {
                          const owner = ownerByAlias.get(normalizeAlias(item.alias));
                          return owner ? { ...item, conflict_with: owner } : item;
                      }),
                  }
                : prev
        );
    };

    /** Handler for submitting all edited data to server side by calling POST API.  */
    const handleSaveButton = () => {
        // Ignore repeated clicks while a save request is in flight so the same payload
        // is not emitted twice (two concurrent server transactions collide on preset name).
        if (saveLoad) return;

        // Combine trade partner data and preset into il_trade_partner.
        const il_trade_partner = { ...tradePartnerForm, il_preset: applySettingForm };
        // Aliases go out only once loaded; without the key the server leaves them as they are.
        // A missing id marks an alias added in this dialog, and saved ids left out are removed.
        if (Array.isArray(tradePartnerForm.il_trade_partner_alias)) {
            il_trade_partner.il_trade_partner_alias = tradePartnerForm.il_trade_partner_alias.map((item) => ({
                id: typeof item.id === 'number' ? item.id : null,
                alias: item.alias,
            }));
        } else {
            delete il_trade_partner.il_trade_partner_alias;
        }
        const updatedTradePartner = { il_trade_partner: il_trade_partner, presetNoteForm: presetNoteData };

        // Validate update condition before making WebSocket call.
        const validationRules = {
            partner_type: tradePartnerForm.partner_type,
            fullname: tradePartnerForm.fullname,
            name: tradePartnerForm.name,
            address_one: tradePartnerForm.address_one,
        };

        const isValid = validateFields(validationRules, setTradePartnerValidator);

        if (!isValid) {
            dispatch(setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'warning' }));
            return;
        }

        // Show loading and lock Save until the server answers. guardSocketAck releases the
        // lock on success, failure, ack timeout and disconnect, so it cannot stay stuck.
        setSaveLoad(true);
        const ack = guardSocketAck(dispatch, {
            onSuccess: (receivedData) => {
                dispatch(updateTradePartner(receivedData.data));
                // 서버 응답의 presetNoteForm은 il_preset_table_id 키를 사용하므로 il_preset_id로 변환 후 Redux 갱신
                const savedNotes = (receivedData.data?.presetNoteForm ?? []).map((note) => ({
                    ...note,
                    il_preset_id: note.il_preset_table_id,
                }));
                if (savedNotes.length > 0) {
                    dispatch(setPresetNote(savedNotes));
                }
                dispatch(setSnackbar({ open: true, message: 'Update Successful.', severity: 'success' }));
                resetDialogState();
            },
            onFailure: markAliasConflicts,
            onFinally: () => setSaveLoad(false),
        });

        updateSocketData(dispatch, '', 'row', 'il_trade_partner', '', updatedTradePartner, ack.onSuccess, ack.onFailure);
    };

    /** Handler for closing the dialog. */
    const handleEditDialogClose = () => {
        // Reset the working state if:
        //  1. The user confirms changes by clicking "OK" in the confirmation dialog, or
        //  2. There are no changes compared to the original state.

        if (dirtyFlags.isOriginDirty || dirtyFlags.isPresetDirty) {
            const confirmLeaveEdit = window.confirm('Your unsaved changes will be lost. Are you sure?');
            if (!confirmLeaveEdit) return;
        }
        resetDialogState();
    };

    /** Function to detect whether user input has occurred. */
    const isUpdated = useMemo(() => {
        return (
            !isEqual(tradePartnerForm, originalData.tradePartner) ||
            !isEqual(applySettingForm, originalData.applySetting)
        );
    }, [tradePartnerForm, applySettingForm, originalData]);

    /** useEffect for updating tradePartnerData when the user's selected row changes. */
    useEffect(() => {
        if (selectedRow) {
            // Aliases come only from their own read below, never from a list cached on the row.
            const { il_trade_partner_alias, ...editingRow } = tradePartnerData.find(
                (value) => value.id === selectedRow.id
            );

            setTradePartnerForm(editingRow);
            setApplySettingForm(editingRow.il_preset);
            // Save first load trade partner info in originalData state.
            setOriginalData({ tradePartner: editingRow, applySetting: editingRow.il_preset });
        }
    }, [selectedRow, editOpen]);

    /** useEffect to read selected trade partner's aliases into both the form and its baseline. */
    useEffect(() => {
        if (!selectedRow) return;
        const partnerId = selectedRow.id;
        // null marks a failed read: the list stays locked because the saved aliases are unknown.
        const applyAliases = (aliases) => {
            const withAliases = (row) =>
                row?.id === partnerId ? { ...row, il_trade_partner_alias: aliases } : row;
            setTradePartnerForm(withAliases);
            setOriginalData((prev) => ({ ...prev, tradePartner: withAliases(prev.tradePartner) }));
        };
        readSocketData(
            dispatch,
            '',
            'row',
            'il_trade_partner_alias',
            '',
            partnerId,
            (receivedData) => applyAliases(Array.isArray(receivedData) ? receivedData : []),
            () => applyAliases(null)
        );
    }, [selectedRow]);

    /** useEffect to read selected trade partner's notes. */
    useEffect(() => {
        if (selectedRow) {
            readSocketData(dispatch, '', 'row', 'il_preset_table_note', '', selectedRow.id, (receivedData) => {
                dispatch(setPresetNote(receivedData));
            });
        }
    }, [selectedRow]);

    /** Partner type labels only: same server read as NewTradePartnerDialog (not used for other fields). */
    useEffect(() => {
        if (!editOpen || !socketConnected) {
            return;
        }
        readSocketData(
            dispatch,
            '',
            'cell',
            'il_trade_partner',
            'partnerTypeOptions',
            '',
            (receivedData) => {
                if (!Array.isArray(receivedData) || receivedData.length === 0) {
                    return;
                }
                setPartnerTypeOptions(receivedData);
            },
        );
    }, [editOpen, socketConnected, dispatch]);

    /** Reflect changes in isUpdated to activeSave. */
    useEffect(() => {
        setDirtyFlags((prev) => ({ ...prev, isOriginDirty: isUpdated }));
    }, [isUpdated]);

    return (
        <>
            <CircularLoading loading={saveLoad} />
            <Paper>
                <Dialog
                    onClose={handleEditDialogClose}
                    open={editOpen}
                    fullScreen
                    TransitionComponent={SlideUpTransition}
                    PaperProps={{ sx: { borderRadius: '18px', m: '0' } }}
                    slotProps={{ backdrop: { style: { backgroundColor: 'rgba(0,0,0,0)' } } }}
                    sx={{
                        '& .MuiDialog-paper': {
                            position: 'absolute',
                            top: '40%',
                            height: '560px',
                            transform: 'none',
                        },
                    }}
                >
                    <DialogHeader
                        title="EDIT"
                        onClose={handleEditDialogClose}
                        onSave={handleSaveButton}
                        backgroundColor="#00647e"
                        saveDisabled={!dirtyFlags.isOriginDirty || saveLoad}
                        rightChildren={
                            <>
                                <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 600 }}>ACTIVE</Typography>
                                <FormControlLabel
                                    control={
                                        <ActiveSwitch
                                            sx={{ ml: '5px', mr: '5px' }}
                                            color="primary.light"
                                            checked={tradePartnerForm.status === true}
                                            onChange={handleActiveChange}
                                        />
                                    }
                                />
                            </>
                        }
                    />
                    <Grid container>
                        <Grid size={5}>
                            <PartnerInfo
                                tradePartnerForm={tradePartnerForm}
                                setTradePartnerForm={setTradePartnerForm}
                                tradePartnerValidator={tradePartnerValidator}
                                partnerTypeOptions={partnerTypeOptions}
                                purpose="edit"
                                spacing={2}
                            />
                        </Grid>
                        <Grid size={7}>
                            <ApplySetting
                                applySettingData={applySettingForm}
                                setApplySettingData={setApplySettingForm}
                                dirtyFlags={dirtyFlags}
                                setDirtyFlags={setDirtyFlags}
                                selectedRow={selectedRow}
                            />
                        </Grid>
                    </Grid>
                </Dialog>
            </Paper>
            <CustomSnackbar />
        </>
    );
}
